import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  animationFrames,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs';
import { Employments } from 'server/shared/collections/employments';
import { getDispoEmploymentsQuery } from 'server/shared/helpers/employments-query/getDispoEmploymentsQuery';
import { DispoEmploymentTabIdEnum } from 'server/shared/models/DispoEmploymentTabId.enum';
import {
  Employment,
  EmploymentStatusEnum,
} from 'server/shared/models/employment';
import { RoleEnum } from 'server/shared/models/role.enum';
import { User } from 'server/shared/models/user';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { ToastService } from '../../services/toast/toast.service';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../shared/subscription.service';

@Component({
  selector: 'app-employments-index',
  templateUrl: './employments-index.component.html',
  styleUrls: ['./employments-index.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentsIndexComponent implements OnInit, OnDestroy {
  currentUser$ = this.userService.readyDispo$;
  limit = 100;
  showCanceledJobs = false;
  EmploymentStatusEnum = EmploymentStatusEnum;

  private destroyed$ = new Subject<void>();

  tabParam$: Observable<DispoEmploymentTabIdEnum> = this.route.queryParams.pipe(
    map((params) => params.tab ?? DispoEmploymentTabIdEnum.OPEN),
    distinctUntilChanged(),
  );

  searchParam$: Observable<string | undefined> = this.route.queryParams.pipe(
    map((params) => params.searchText),
    distinctUntilChanged(),
  );

  rangeParam$: Observable<string | undefined> = this.route.queryParams.pipe(
    map((params) => params.range ?? 'ALL'),
    distinctUntilChanged(),
  );

  showCanceledParam$: Observable<boolean> = this.route.queryParams.pipe(
    map((params) => params['show-canceled'] === 'true'),
    distinctUntilChanged(),
  );
  employmentsQuery$ = combineLatest([
    this.tabParam$,
    this.searchParam$,
    this.rangeParam$,
    this.showCanceledParam$,
    this.userService.readyUser$,
  ]).pipe(
    map(([tab, search, range, showCanceled, user]) => {
      this.showCanceledJobs = showCanceled;
      let query = getDispoEmploymentsQuery(
        user,
        tab,
        search,
        range,
        showCanceled,
      );
      if (tab === DispoEmploymentTabIdEnum.ARCHIVED) {
        this.subscriptionService.subscribeArchivedEmployments(query);
      }
      return query;
    }),
  );

  loaderFinished$ = new Subject<void>();
  // load all users when loader finished to reduce unloading and reloading of usersOfEmployment
  loadAllEmploymentUsersEffect$ = this.loaderFinished$.pipe(
    takeUntil(this.destroyed$),
    switchMap(() => this.subscriptionService.employmentUsersReady$),
    shareReplay(1),
  );

  employmentsLoader$ = this.employmentsQuery$.pipe(
    filter(filterIsNotEmpty),
    map((query) => ({
      query,
      loadedEmploymentIds: new Map<string, boolean>(),
    })),
    switchMap(({ query, loadedEmploymentIds }) =>
      Employments.find(query, { limit: this.limit }).pipe(
        map((employments) => ({ employments, loadedEmploymentIds })),
      ),
    ),
    debounceTime(50),
    // delay leads to more reliable smooth tab-switching animation
    delay(100),
    switchMap(({ employments, loadedEmploymentIds }) =>
      animationFrames().pipe(
        map(() => ({
          employmentsToLoad: employments.filter(
            (emp) => !loadedEmploymentIds.has(emp._id),
          ),
        })),
        takeWhile(
          ({ employmentsToLoad }) => employmentsToLoad.length > 0,
          true,
        ),
        map(({ employmentsToLoad }) => {
          // last item loaded
          if (employmentsToLoad.length === 1) {
            this.loaderFinished$.next();
          }
          if (employmentsToLoad[0]) {
            loadedEmploymentIds.set(employmentsToLoad[0]._id, true);
          }
          return { loadedEmploymentIds, employments };
        }),
      ),
    ),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  loadedEmploymentIds$ = this.employmentsLoader$.pipe(
    map(({ loadedEmploymentIds }) => loadedEmploymentIds),
    shareReplay(1),
  );

  employments$ = this.employmentsLoader$.pipe(
    map(({ employments }) => employments),
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
  );

  tabs: { label: string; value: DispoEmploymentTabIdEnum; svgIcon: string }[] =
    [
      {
        label: 'open',
        value: DispoEmploymentTabIdEnum.OPEN,
        svgIcon: 'inbox',
      },
      {
        label: 'booked',
        value: DispoEmploymentTabIdEnum.BOOKED,
        svgIcon: 'board-booked-star',
      },
      {
        label: 'archived',
        value: DispoEmploymentTabIdEnum.ARCHIVED,
        svgIcon: 'archive-box',
      },
    ];

  dateRangeChips = ['ALL', 'TODAY', 'MONTH', 'NEXT_MONTH', 'CUSTOM'];

  $isLoadingEmployments = this.tabParam$.pipe(
    switchMap((tab) => {
      return tab === DispoEmploymentTabIdEnum.ARCHIVED
        ? this.subscriptionService.archivedEmploymentsLoading$
        : this.subscriptionService.employments.loading$;
    }),
  );

  constructor(
    private ngZone: NgZone,
    private subscriptionService: SubscriptionService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
  ) {
    this.loadAllEmploymentUsersEffect$.subscribe();
    this.userService.readyUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        if (user.role !== RoleEnum.DISPO) {
          this.router.navigate(['/']);
        }
      });
  }

  ngOnInit(): void {
    combineLatest([this.tabParam$, this.currentUser$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([tab, user]) => {
        if (user) {
          this.applySavedDefaultFilter(tab, user);
        }
      });
    this.subscriptionService.employments.subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  switchTab(tabId: DispoEmploymentTabIdEnum) {
    this.mergeQueryParams({
      tab: tabId,
    });
  }

  mergeQueryParams(queryParams: Params) {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }

  applySavedDefaultFilter(tab: DispoEmploymentTabIdEnum, user: User) {
    const resetParamsObject = {
      searchText: null,
      range: null,
    };
    if (
      !user ||
      user.role !== RoleEnum.DISPO ||
      !user.employmentDefaultFilters ||
      !user.employmentDefaultFilters[tab]
    ) {
      this.mergeQueryParams(resetParamsObject);
      return;
    }
    this.mergeQueryParams({
      ...resetParamsObject,
      ...user.employmentDefaultFilters[tab],
    });
  }

  saveDefaultFilter() {
    combineLatest([this.tabParam$, this.currentUser$])
      .pipe(take(1))
      .subscribe(([tab, user]) => {
        const resetParamsObject = {
          searchText: null,
          range: null,
        };
        Meteor.call('saveDispoEmploymentDefaultFilter', tab, {
          ...resetParamsObject,
          ...this.route.snapshot.queryParams,
        });
        this.toastService.add({
          type: 'SUCCESS',
          message: 'savedDefaultFilter',
          timeout: 5000,
        });
      });
  }

  trackById(index: number, object: Employment): string {
    return object._id;
  }

  toggleShowCanceledJobs(event: MatCheckboxChange) {
    this.mergeQueryParams({ 'show-canceled': event.checked });
  }
}
